/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router';
import type PlaygroundApiService from 'embercom/services/playground-api';
import { ResponseError } from 'embercom/lib/inbox/requests';
import type Transition from '@ember/routing/transition';
import type Store from '@ember-data/store';
import { hasFeature } from 'embercom/helpers/has-feature';

type ModelParams = {
  test_id: string;
};

export default class TestingTestStandaloneRoute extends Route {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare intercomEventService: $TSFixMe;
  @service declare playgroundApi: PlaygroundApiService;
  @service declare permissionsService: $TSFixMe;
  @service declare store: Store;

  get titleToken() {
    return this.intl.t('ai-agent.sub-menu.playground.title');
  }

  async beforeModel(transition: Transition) {
    if (!this.appService.app.canUseFinPlayground) {
      this.router.transitionTo('apps.app.standalone.deploy.overview');
    }

    if (!transition.to.queryParams.skipAdminValidation) {
      try {
        await this.playgroundApi.validateAdminPermissions();
      } catch (err) {
        if (err instanceof ResponseError && err.response.status === 401) {
          // The permissions service will handle showing the modal
          // and redirecting when there is no from route
          await this.permissionsService.cancelTransitionAndShowPermissionModal(
            'can_access_all_conversations',
            transition,
            'apps.app.standalone.deploy.overview',
          );

          // If there is a from route we still want to transition to it
          // Otherwise we will stay on the current route with a loading spinner
          if (transition.from?.name) {
            this.router.transitionTo(transition.from.name);
          }

          return;
        }
        throw err;
      }
    }
  }

  testIdIsValid(testId: number): boolean {
    return !isNaN(testId) && testId > 0;
  }

  async model(params: ModelParams, transition: Transition) {
    let testId = Number(params.test_id);

    if (!transition.to.queryParams.skipSummaryLoad) {
      await this.playgroundApi.reloadPlaygroundSummary();
    }

    if (
      this.testIdIsValid(testId) &&
      this.playgroundApi.playgroundSummary?.groupSummaries.some((summary) => summary.id === testId)
    ) {
      // If the test_id is valid and corresponds to an existing record, stay on the current route
      // and relaod the playground so that the matching playground exists on the playground api
      if (hasFeature('fin-playground-escalation', this.appService)) {
        await Promise.all([
          this.playgroundApi.reloadPlayground(testId),
          this.store.findAll('ai-agent/escalation-rule'),
        ]);
      } else {
        this.playgroundApi.reloadPlayground(testId);
      }
    } else {
      this.router.transitionTo('apps.app.standalone.testing.index');
    }

    return {};
  }
}
